import type { EmailRecipientDetail } from "../api/emails";

// Display helpers for email recipients. An email_log row carries two
// lists: `recipients` (where the mail was actually delivered — in
// non-prod every send is redirected to a notifications inbox, so this is
// often not the real audience) and `recipient_details` (the intended
// audience with real names, recorded at send time; newer rows only).
// Display prefers the intended audience and falls back to the delivery
// list, deriving a name from the address for rows that predate details.

export interface RecipientEntry {
  email: string;
  name: string | null;
}

/**
 * Best-available display name for a bare address: title-case the
 * local-part's segments — "asha.rao@…" -> "Asha Rao",
 * "raven-notifications@…" -> "Raven Notifications".
 */
export function nameFromAddress(address: string | null | undefined): string {
  return String(address ?? "")
    .split("@")[0]
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(" ");
}

/** Normalized display list, intended audience first choice. */
export function displayRecipients(
  recipients: string[] | null | undefined,
  recipientDetails?: EmailRecipientDetail[] | null,
): RecipientEntry[] {
  if (Array.isArray(recipientDetails)) {
    const entries = recipientDetails
      .filter((d) => d?.email)
      .map((d) => ({ email: d.email, name: d.name || null }));
    if (entries.length > 0) return entries;
  }
  return (recipients ?? []).map((email) => ({ email, name: null }));
}

export function recipientEntryName(entry?: RecipientEntry): string {
  if (!entry) return "";
  return entry.name || nameFromAddress(entry.email);
}

/**
 * Row summary: the first recipient's name plus "+n more" when the email
 * fanned out. The full list belongs in the detail dialog, not the rail.
 */
export function recipientSummary(
  recipients: string[] | null | undefined,
  recipientDetails?: EmailRecipientDetail[] | null,
): string {
  const entries = displayRecipients(recipients, recipientDetails);
  if (entries.length === 0) return "No recipients recorded";
  const first = recipientEntryName(entries[0]);
  return entries.length === 1
    ? first
    : `${first} +${entries.length - 1} more`;
}
